import React from 'react';
import {ofType} from 'redux-observable';
import {Observable, of} from 'rxjs';
import {List} from 'immutable';
import i18next from 'i18next';
import {catchError, flatMap} from 'rxjs/operators';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDialogObservable, showAlertObservable, DialogChoice} from 'web-app/react/dialog';
import * as AccountItemObservables from 'web-app/react/entities/account-items/epics';
import * as BillPayerObservables from 'web-app/react/entities/bill-payers/epics';
import * as RecurringPaymentObservables from 'web-app/react/entities/recurring-payments/epics';
import {createOrUpdateObservable} from 'web-app/react/entities/bill-payers/epics';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import PaymentBehaviors from 'web-app/behaviors/payment-behavior-ids';
import * as AccountItemMoveModalActions from 'web-app/react/modals-container/account-item-move/actions';
import AccountItem from 'web-app/react/entities/account-items/model';
import Markdown from 'web-app/react/components/markdown/markdown';

import * as Actions from './actions';

const fetchData = action$ =>
    action$.ofType(Actions.fetchAll.type).mergeMap(({payload}) => {
        return Observable.combineLatest(
            BillPayerObservables.fetchBillPayerObservable(payload.institutionId, payload.billPayerId),
            AccountItemObservables.fetchInvoicesForTargetObservable(
                `Famly.Daycare:Institution|${payload.institutionId}`,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                [payload.billPayerId],
            ),
        )
            .flatMap(([billPayerSuccessAction, invoicesSuccessAction]) => {
                return Observable.of(billPayerSuccessAction).concat([
                    Actions.fetchAllSuccess.action(List(invoicesSuccessAction?.payload.invoices)),
                ]);
            })
            .catch(e => Observable.of(Actions.fetchAllFailed.action(e)));
    });

const fetchBalanceData = action$ =>
    action$.pipe(
        ofType(Actions.fetchBalance.type),
        flatMap(({payload}) => {
            return AccountItemObservables.fetchBillPayerBalanceObservable(
                payload.institutionId,
                payload.billPayerId,
                undefined,
                payload.limit,
            ).pipe(
                flatMap(successAction => {
                    const {payload} = successAction;
                    return of(
                        Actions.fetchBalanceSuccess.action(
                            List(payload.invoicing.items).map(AccountItem.fromAPI),
                            payload.invoicing.balance,
                        ),
                    );
                }),
                catchError(e => Actions.fetchBalanceFailed.action(e)),
            );
        }),
    );

const createPayment = action$ =>
    action$
        .ofType(Actions.createPayment.type)
        .debounceTime(500)
        .mergeMap(({payload}) => {
            // We don't want to update the voucher company when the user select an existing company
            if (payload.voucherCompany.billPayerId) {
                return AccountItemObservables.addPaymentObservable(payload.institutionId, payload.billPayerId, {
                    ...payload.payment,
                    voucherCompany: {
                        voucherCompanyId: payload.voucherCompany.billPayerId,
                    },
                })
                    .flatMap(paymentSuccess => List([Actions.createPaymentSuccess.action(), paymentSuccess]))
                    .catch(e => Observable.of(Actions.createPaymentFailed.action(e)));
            }
            return createOrUpdateObservable(payload.institutionId, null, payload.voucherCompany)
                .flatMap(billPayerSuccess => {
                    return AccountItemObservables.addPaymentObservable(payload.institutionId, payload.billPayerId, {
                        ...payload.payment,
                        voucherCompanyId: billPayerSuccess.payload.billPayer.billPayerId,
                    }).flatMap(paymentSuccess =>
                        List([Actions.createPaymentSuccess.action(), billPayerSuccess, paymentSuccess]),
                    );
                })
                .catch(e => Observable.of(Actions.createPaymentFailed.action(e)));
        });

const updatePayment = action$ =>
    action$
        .ofType(Actions.updatePayment.type)
        .debounceTime(500)
        .mergeMap(({payload}) => {
            // We don't want to update the voucher company when the user select an existing company
            if (payload.voucherCompany.billPayerId) {
                return AccountItemObservables.updatePaymentObservable(
                    payload.institutionId,
                    payload.billPayerId,
                    {
                        ...payload.payment,
                        voucherCompany: {
                            voucherCompanyId: payload.voucherCompany.billPayerId,
                        },
                    },
                    payload.oldPayment,
                )
                    .flatMap(paymentSuccess => List([Actions.updatePaymentSuccess.action(), paymentSuccess]))
                    .catch(e => Observable.of(Actions.updatePaymentFailed.action(e)));
            }
            return createOrUpdateObservable(payload.institutionId, null, payload.voucherCompany)
                .flatMap(billPayerSuccess => {
                    return AccountItemObservables.updatePaymentObservable(
                        payload.institutionId,
                        payload.billPayerId,
                        {
                            ...payload.payment,
                            voucherCompanyId: billPayerSuccess.payload.billPayer.billPayerId,
                        },
                        payload.oldPayment,
                    ).flatMap(paymentSuccess =>
                        List([Actions.updatePaymentSuccess.action(), billPayerSuccess, paymentSuccess]),
                    );
                })
                .catch(e => Observable.of(Actions.updatePaymentFailed.action(e)));
        });

const confirmPayment = action$ =>
    action$.ofType(Actions.confirmPayment.type).mergeMap(({payload}) =>
        AccountItemObservables.addPaymentObservable(payload.institutionId, payload.billPayerId, payload.payment)
            .flatMap(action => [
                action,
                GlobalEventActions.updateSuccess.action(i18next.t('vouchers.paymentConfirmed')),
            ])
            .catch(e => Observable.of(Actions.confirmPaymentFailed.action(e))),
    );

const denyPayment = action$ =>
    action$.ofType(Actions.denyPayment.type).mergeMap(({payload}) => {
        return showDialogObservable({title: '', description: i18next.t('vouchers.confirmVoucherDeny')}).mergeMap(
            selection => {
                if (selection === DialogChoice.OK) {
                    return RecurringPaymentObservables.denyPaymentObservable(
                        payload.institutionId,
                        payload.billPayerId,
                        payload.payment,
                    )
                        .mergeMap(denyPaymentSuccess => {
                            return AccountItemObservables.fetchBillPayerBalanceObservable(
                                payload.institutionId,
                                payload.billPayerId,
                            ).flatMap(balanceSuccess => [
                                Actions.denyPaymentSuccess.action(),
                                denyPaymentSuccess,
                                balanceSuccess,
                                GlobalEventActions.updateSuccess.action(i18next.t('vouchers.paymentDenied')),
                            ]);
                        })
                        .catch(e => Observable.of(Actions.denyPaymentFailed.action(e)));
                } else {
                    return Observable.of(Actions.denyPaymentAborted.action());
                }
            },
        );
    });

const movePayment = action$ =>
    action$.ofType(Actions.showMovePaymentModal.type).mergeMap(({payload}) => {
        const behavior = payload.payment.behaviors.behaviorForId(PaymentBehaviors.CanMovePayment);

        const errors = behavior.payload.warnings.filter(w => w.severity === 'ERROR');
        const warnings = behavior.payload.warnings.filter(w => w.severity === 'WARNING');

        const errorTexts = errors.map(e => e.title);
        const warningTexts = warnings.map(w => w.title);

        const show = Observable.of(
            AccountItemMoveModalActions.show.action(
                payload.institutionId,
                payload.billPayerId,
                AccountItem.Constants.Payment,
                payload.payment.id,
            ),
        );

        if (errors.length > 0) {
            return showAlertObservable({
                title: '',
                description: React.createElement(Markdown, {source: errorTexts.join('\n  \n  ')}),
            }).mergeMap(() => Observable.empty());
        } else {
            return warnings.length > 0
                ? showDialogObservable({
                      title: i18next.t('accountItems.move.transferPaymentWarningsTitle'),
                      description: React.createElement(Markdown, {
                          source: i18next.t('accountItems.move.transferPaymentWarningsDescription', {
                              warnings: warningTexts.join('\n  \n  '),
                          }),
                      }),
                  }).mergeMap(selection => (selection === DialogChoice.OK ? show : Observable.empty()))
                : show;
        }
    });

export default combineEpics(
    confirmPayment,
    denyPayment,
    createPayment,
    updatePayment,
    fetchData,
    movePayment,
    fetchBalanceData,
);
